import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './App.css';
import DiceTray from './components/DiceTray';
import gruntIcon from './assets/icons/grunt-goblin.png';
import rulesLawyerIcon from './assets/icons/rules-lawyer.png';

const SESSION_ID = process.env.REACT_APP_SESSION_ID || 'sess-test-001';

// Lightweight markdown renderer for SAI narratives
function renderMarkdown(text, suffix) {
  if (!text) return suffix || null;
  // Split into lines
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="msg-list">{listItems}</ul>);
      listItems = [];
    }
  };

  const formatInline = (line, keyPrefix) => {
    // Bold, italic, bold-italic
    const parts = [];
    let remaining = line;
    let i = 0;
    const regex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={`${keyPrefix}-${i++}`}>{remaining.slice(lastIndex, match.index)}</span>);
      }
      if (match[2]) parts.push(<strong key={`${keyPrefix}-${i++}`}><em>{match[2]}</em></strong>);
      else if (match[3]) parts.push(<strong key={`${keyPrefix}-${i++}`}>{match[3]}</strong>);
      else if (match[4]) parts.push(<em key={`${keyPrefix}-${i++}`}>{match[4]}</em>);
      else if (match[5]) parts.push(<code key={`${keyPrefix}-${i++}`} className="msg-code">{match[5]}</code>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
      parts.push(<span key={`${keyPrefix}-${i++}`}>{remaining.slice(lastIndex)}</span>);
    }
    return parts.length > 0 ? parts : remaining;
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    // Bullet list
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      const content = trimmed.replace(/^[-•]\s+/, '');
      listItems.push(<li key={`li-${idx}`}>{formatInline(content, `li-${idx}`)}</li>);
      return;
    }
    // Numbered list
    if (/^\d+\.\s/.test(trimmed)) {
      const content = trimmed.replace(/^\d+\.\s+/, '');
      listItems.push(<li key={`li-${idx}`}>{formatInline(content, `li-${idx}`)}</li>);
      return;
    }
    flushList();
    // Headers
    if (trimmed.startsWith('### ')) {
      elements.push(<div key={`h-${idx}`} className="msg-h3">{formatInline(trimmed.slice(4), `h-${idx}`)}</div>);
    } else if (trimmed.startsWith('## ')) {
      elements.push(<div key={`h-${idx}`} className="msg-h2">{formatInline(trimmed.slice(3), `h-${idx}`)}</div>);
    } else if (trimmed === '') {
      elements.push(<div key={`br-${idx}`} className="msg-break" />);
    } else {
      elements.push(<div key={`p-${idx}`} className="msg-para">{formatInline(trimmed, `p-${idx}`)}</div>);
    }
  });
  flushList();
  // Inject suffix (e.g., 🎲 icon) inline at end of last element
  if (suffix && elements.length > 0) {
    const last = elements[elements.length - 1];
    elements[elements.length - 1] = React.cloneElement(last, {}, ...(Array.isArray(last.props.children) ? last.props.children : [last.props.children]), suffix);
  }
  return elements;
}
const REALM_ID_INITIAL = '';  // Set dynamically when LORE is uploaded
const PSP_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

function App() {
  // ========================================================================
  // Authentication State
  // ========================================================================
  const [username, setUsername] = useState(null);
  const [loginInput, setLoginInput] = useState('');
  const [isLoading, setIsLoading] = useState(true); // Track initialization

  // ========================================================================
  // Chat & Message State
  // ========================================================================
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [sceneId, setSceneId] = useState('');
  const [target, setTarget] = useState('scene');
  const [loading, setLoading] = useState(false);
  const [aiPhase, setAiPhase] = useState('thinking'); // 'thinking' (SAI) or 'narrating' (VAI)
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [lastPlayerMessage, setLastPlayerMessage] = useState('');
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // File upload state
  const [keysFile, setKeysFile] = useState(null);
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [keysValidation, setKeysValidation] = useState(null);
  const [loreFile, setLoreFile] = useState(null);
  const [adventureFile, setAdventureFile] = useState(null);
  const [characterFile, setCharacterFile] = useState(null);
  const [uploading, setUploading] = useState({ keys: false, lore: false, adventure: false, character: false });
  const [loreLoaded, setLoreLoaded] = useState(false);
  const [adventureLoaded, setAdventureLoaded] = useState(false);
  const [characterLoaded, setCharacterLoaded] = useState(false);
  const [saiReady, setSaiReady] = useState(false);       // true after first SAI narrative arrives
  const [characterFirstName, setCharacterFirstName] = useState(null);
  const [characterData, setCharacterData] = useState(null);
  const [liveHp, setLiveHp] = useState(null);             // mutable HP (null = use characterData)
  const [usedSpells, setUsedSpells] = useState(new Set()); // expended spell names
  const [liveSpells, setLiveSpells] = useState(null);      // prepared spells (null = use characterData)
  const [extraItems, setExtraItems] = useState([]);        // items gained during play
  const [removedItems, setRemovedItems] = useState(new Set()); // items lost during play
  const [baseUpdates, setBaseUpdates] = useState([]);      // {match, text} replacements for base equipment
  const [sceneName, setSceneName] = useState('');
  const [realmId, setRealmId] = useState(REALM_ID_INITIAL);
  const [playtestMode, setPlaytestMode] = useState(false);
  const [sheetTab, setSheetTab] = useState('stats');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [diceOpen, setDiceOpen] = useState(false);
  const [pendingDiceRequest, setPendingDiceRequest] = useState(null);
  const [combatState, setCombatState] = useState(null);
  const [diceLogOpen, setDiceLogOpen] = useState(null); // index of message showing dice log
  const [imageLoading, setImageLoading] = useState(null); // index of message generating image
  const [lightboxImage, setLightboxImage] = useState(null); // {b64, turn, prompt, title, id}
  const [imageExpiryWarning, setImageExpiryWarning] = useState(null);
  const [sessionImages, setSessionImages] = useState([]); // [{id, turn, title, subject, expiring, ...}]
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [subjectPicker, setSubjectPicker] = useState(null); // {msgIndex, narrative, turn, subjects, loading}
  const [customImagePrompt, setCustomImagePrompt] = useState(null); // {msgIndex, narrative, turn} — freeform input
  const [customImageText, setCustomImageText] = useState('');
  const keysDropRef = useRef(null);
  const loreDropRef = useRef(null);
  const adventureDropRef = useRef(null);
  const characterDropRef = useRef(null);

  // ========================================================================
  // Initialize app: Load username from localStorage on mount
  // ========================================================================
  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsername(storedUsername);
    }
    setIsLoading(false);
  }, []);

  // ========================================================================
  // Handle Login: Store username & transition to chat
  // ========================================================================
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginInput.trim()) {
      alert('Please enter a username');
      return;
    }

    const trimmedUsername = loginInput.trim();
    setLoginInput('');

    localStorage.setItem('username', trimmedUsername);
    setUsername(trimmedUsername);
  };

  // ========================================================================
  // Handle Logout: Clear username & show login form
  // ========================================================================
  const handleLogout = () => {
    localStorage.removeItem('username');
    setUsername(null);
    setMessages([]);
    // Full reset — clean slate on next login
    setKeysFile(null);       setKeysLoaded(false);  setKeysValidation(null);
    setLoreFile(null);       setLoreLoaded(false);
    setAdventureFile(null);  setAdventureLoaded(false);
    setCharacterFile(null);  setCharacterLoaded(false);  setCharacterFirstName(null);  setCharacterData(null);
    setLiveHp(null);  setUsedSpells(new Set());  setLiveSpells(null);  setExtraItems([]);  setRemovedItems(new Set());  setBaseUpdates([]);
    setSceneName('');        setSceneId('');
    setRealmId(REALM_ID_INITIAL);
    setPlaytestMode(false);
    setSaiReady(false);
  };

  // Loading timer — counts seconds while waiting for AI response
  useEffect(() => {
    if (!loading) { setLoadingSeconds(0); return; }
    const interval = setInterval(() => setLoadingSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [loading]);

  // Socket.IO connection — deferred until character is loaded
  useEffect(() => {
    // Only connect once the player is fully ready (logged in + character + scene set)
    if (!username || !characterLoaded || !sceneId) return;

    const socket = io(PSP_URL, {
      query: {
        session_id: SESSION_ID,
        realm_id: realmId,
        scene_id: sceneId,
        username: characterFirstName || username
      },
      reconnection: true,
      reconnectionDelay: 3000,
      transports: ['polling', 'websocket']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to PSP via Socket.IO');
      setConnected(true);
    });

    socket.on('connected', (data) => {
      console.log('Confirmed:', data);
    });

    socket.on('message', (data) => {
      console.log('Message received:', data);

      // Handle AI progress updates (SAI done → VAI starting)
      if (data.type === 'ai_progress' && data.phase === 'narrating') {
        setAiPhase('narrating');
        return;
      }

      // Handle Rules Lawyer notifications
      if (data.type === 'rules_lawyer') {
        setMessages(prev => [...prev, {
          role: 'system',
          text: data.content || 'The Rules Lawyer spotted an issue — recalculating...',
          isSystem: true,
          isRulesLawyer: true,
          timestamp: data.timestamp || new Date().toISOString()
        }]);
        return;
      }

      // Handle async map updates from Grunt (no chat message — just update the map drawer)
      if (data.type === 'map_update' && data.map && data.map.tiles) {
        setMapData(data.map);
        console.log('Map updated via Grunt:', data.map.width + 'x' + data.map.tiles.length, (data.map.entities || []).length + ' entities');
        return;
      }

      // Skip "entered the realm" / "left the realm" system messages for self
      if (data.type === 'system' && data.content && (
        data.content.includes('has entered the realm') ||
        data.content.includes('has left the realm')
      )) {
        console.log('Suppressing self join/leave system message');
        return;
      }

      // Skip empty messages
      if (!data.content && !data.text) {
        console.log('Skipping empty message:', data);
        return;
      }

      // Handle AI errors (timeout, rate limit, etc.) — show error with retry
      if (data.ai_error) {
        const retryAction = data.retry_action;
        setMessages(prev => [...prev, {
          role: 'system',
          text: data.content || 'The AI encountered an error.',
          timestamp: data.timestamp || new Date().toISOString(),
          isSystem: true,
          aiError: data.ai_error,
          retryAction,
        }]);
        setLoading(false);
        return;
      }

      const msg = {
        role: data.sender === 'SAI' || data.sender === 'Storyteller' || data.sender === 'Game Master' ? 'assistant' :
              data.type === 'system' ? 'system' : 'player',
        text: data.content || data.text || '',
        scope: data.scope,
        sender: data.sender,
        turn: data.turn || null,
        timestamp: data.timestamp || new Date().toISOString(),
        isSystem: data.type === 'system',
        consequence: data.consequence || null,
        diceUsed: data.dice_used || null,
        diceRequest: (data.dice_requests && data.dice_requests.length > 0) ? data.dice_requests[0] : null
      };
      setMessages(prev => [...prev, msg]);
      // Unlock chat on first SAI message; clear thinking indicator
      if (msg.role === 'assistant') {
        setSaiReady(true);
        setLoading(false);
      }
      // Handle scene transitions from SAI
      if (data.new_scene && data.new_scene.scene_id) {
        setSceneId(data.new_scene.scene_id);
        setSceneName(data.new_scene.scene_name || data.new_scene.scene_id);
        // Tell PSP to update room subscriptions
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('scene_change', { scene_id: data.new_scene.scene_id });
        }
      }
      // Apply state changes from SAI
      if (data.state_changes) {
        const sc = data.state_changes;
        if (sc.hp_delta) {
          setLiveHp(prev => {
            const next = (prev ?? 10) + sc.hp_delta;
            return Math.max(0, next);
          });
        }
        if (sc.spells_prepared && sc.spells_prepared.length > 0) {
          setLiveSpells(sc.spells_prepared);
          setUsedSpells(new Set()); // fresh slate on new preparation
        }
        if (sc.spells_used && sc.spells_used.length > 0) {
          setUsedSpells(prev => {
            const next = new Set(prev);
            sc.spells_used.forEach(s => next.add(s));
            return next;
          });
        }
        if (sc.spells_recovered && sc.spells_recovered.length > 0) {
          setUsedSpells(prev => {
            const next = new Set(prev);
            sc.spells_recovered.forEach(s => next.delete(s));
            return next;
          });
        }
        if (sc.items_gained && sc.items_gained.length > 0) {
          setExtraItems(prev => [...prev, ...sc.items_gained]);
        }
        if (sc.items_lost && sc.items_lost.length > 0) {
          sc.items_lost.forEach(item => {
            // Try to remove from extra items first (gained during play)
            setExtraItems(prev => {
              const idx = prev.indexOf(item);
              if (idx !== -1) {
                const next = [...prev];
                next.splice(idx, 1);
                return next;
              }
              return prev;
            });
            // Also mark as removed from base equipment
            setRemovedItems(prev => new Set(prev).add(item));
          });
        }
        if (sc.items_updated && sc.items_updated.length > 0) {
          sc.items_updated.forEach(upd => {
            const { match, text } = upd;
            if (!match || !text) return;
            // Try extra items first
            setExtraItems(prev => {
              const idx = prev.findIndex(i => i.toLowerCase().includes(match.toLowerCase()));
              if (idx !== -1) {
                const next = [...prev];
                next[idx] = text;
                return next;
              }
              return prev;
            });
            // Track updates to base equipment
            setBaseUpdates(prev => {
              // Replace existing update for same match, or add new
              const next = prev.filter(u => u.match.toLowerCase() !== match.toLowerCase());
              next.push({ match, text });
              return next;
            });
          });
        }
      }
      // Sync character sheet from authoritative game_state (corrects any drift)
      if (data.game_state) {
        const gs = data.game_state;
        if (gs.current_hp !== null && gs.current_hp !== undefined) setLiveHp(gs.current_hp);
        if (gs.spells_prepared && gs.spells_prepared.length > 0) setLiveSpells(gs.spells_prepared);
        if (gs.used_spells && gs.used_spells.length > 0) setUsedSpells(new Set(gs.used_spells));
        if (gs.extra_items && gs.extra_items.length > 0) setExtraItems(gs.extra_items);
        if (gs.removed_items && gs.removed_items.length > 0) setRemovedItems(new Set(gs.removed_items));
        if (gs.base_updates && gs.base_updates.length > 0) setBaseUpdates(gs.base_updates);
      }
      // Image expiry warnings
      if (data.image_expiry_warning) {
        setImageExpiryWarning(data.image_expiry_warning);
        // Auto-dismiss after 15 seconds
        setTimeout(() => setImageExpiryWarning(null), 15000);
      }
      // Update scene map (fix double-escaped unicode from LLM)
      if (data.map && data.map.tiles) {
        const fixUnicode = (s) => {
          if (typeof s !== 'string') return s;
          try { return JSON.parse('"' + s.replace(/"/g, '\\"') + '"'); }
          catch { return s; }
        };
        const fixedMap = {
          ...data.map,
          entities: (data.map.entities || []).map(e => ({
            ...e,
            icon: fixUnicode(e.icon),
            label: fixUnicode(e.label)
          }))
        };
        setMapData(fixedMap);
      }
      // Handle dice roll requests from SAI
      if (data.dice_requests && data.dice_requests.length > 0) {
        setPendingDiceRequest(data.dice_requests[0]);
        // Don't auto-open dice tray — it blocks the narrative.
        // A roll indicator banner in the chat area prompts the player instead.
      }
      // Handle combat state updates
      if (data.combat_state !== undefined) {
        setCombatState(data.combat_state);
      }
    });

    socket.on('error', (data) => {
      console.error('PSP error:', data.message);
      const errMsg = {
        role: 'system',
        text: `⚠️ Error: ${data.message}`,
        timestamp: new Date().toISOString(),
        isSystem: true
      };
      setMessages(prev => [...prev, errMsg]);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from PSP');
      setConnected(false);
    });

    return () => {
      socket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, realmId, characterLoaded, sceneId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Toast notification timer
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Load session images when realm is set
  useEffect(() => {
    if (realmId) refreshSessionImages();
  }, [realmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================================================================
  // File Upload Handlers
  // ========================================================================

  const showToast = (msg) => {
    setToastMessage(msg);
  };

  const uploadFile = async (file, fileType) => {
    if (!file) return;

    const uploadKey = fileType;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));

    // Show loading message in chat for LORE (Grunt takes a moment)
    if (fileType === 'lore') {
      setMessages(prev => [...prev, {
        role: 'system',
        text: '⚙️ LOADING LORE... Reading your world. Please wait.',
        timestamp: new Date().toISOString(),
        isSystem: true,
        loadingLore: true
      }]);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('realm_id', realmId);
      formData.append('session_id', SESSION_ID);
      formData.append('file_type', fileType);
      formData.append('username', username);

      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Upload failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        if (fileType === 'keys') {
          setKeysFile({ name: data.filename, size: data.size });
          setKeysValidation(data.validation || null);
          setKeysLoaded(true);

          // Show per-slot validation results as system messages
          const v = data.validation || {};
          const lines = [];
          for (const slot of ['engine', 'grunt', 'narration', 'decision']) {
            const r = v[slot];
            if (!r) continue;
            if (r.skipped) lines.push(`${slot}: not configured (will use Engine)`);
            else if (r.valid) lines.push(`${slot}: ${r.provider}/${r.model} -- validated`);
            else lines.push(`${slot}: FAILED (${r.error}) -- will use Engine`);
          }
          setMessages(prev => [
            ...prev,
            { role: 'system', text: `Keys loaded:\n${lines.join('\n')}`, timestamp: new Date().toISOString(), isSystem: true }
          ]);
          if (data.warnings && data.warnings.length > 0) {
            setMessages(prev => [
              ...prev,
              { role: 'system', text: `Warnings: ${data.warnings.join('; ')}`, timestamp: new Date().toISOString(), isSystem: true }
            ]);
          }
        } else if (fileType === 'lore') {
          setLoreFile({ name: data.filename, size: data.size });
          if (data.realm_id) setRealmId(data.realm_id);
          setPlaytestMode(!!data.playtest);

          // Realm identified — no scene yet (waits for ADVENTURE)
          setMessages(prev => [
            ...prev.filter(m => !m.loadingLore),
            { role: 'system', text: `Realm ready: "${data.realm_name}"`, timestamp: new Date().toISOString(), isSystem: true }
          ]);
          setLoreLoaded(true);
        } else if (fileType === 'adventure') {
          setAdventureFile({ name: data.filename, size: data.size });
          setAdventureLoaded(true);

          // Scene determined now that LORE + ADVENTURE are loaded
          if (data.is_new && data.starting_scene) {
            setSceneId(data.starting_scene.scene_id);
            setSceneName(data.starting_scene.scene_name);
          } else if (!data.is_new && data.current_scene) {
            setSceneId(data.current_scene);
            setSceneName(data.current_scene);
            setMessages(prev => [...prev, {
              role: 'system',
              text: `🌍 Welcome back — resuming: ${data.current_scene}`,
              timestamp: new Date().toISOString(),
              isSystem: true
            }]);
          } else {
            // Fallback: ensure scene is always set so socket can connect
            setSceneId('opening-scene');
            setSceneName('Opening Scene');
          }
        } else if (fileType === 'character') {
          setCharacterFile({ name: data.filename, size: data.size });
          setCharacterLoaded(true);
          if (data.first_name) setCharacterFirstName(data.first_name);
          if (data.character_data) {
            setCharacterData(data.character_data);
            setLiveHp(data.character_data.current_hp || 10);
          }

          // Trigger realm init — get Grunt greeting ("are you ready?")
          try {
            const initRes = await fetch(`${BACKEND_URL}/realm/init`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                realm_id: realmId,
                session_id: SESSION_ID,
                username: username
              })
            });
            const initData = await initRes.json();

            if (initData.is_new && initData.greeting) {
              // New game — show "are you ready?" greeting, wait for player response
              setMessages(prev => [...prev, {
                role: 'assistant',
                sender: 'Game Master',
                text: initData.greeting,
                timestamp: new Date().toISOString(),
                isSystem: false
              }]);
              setSaiReady(true);
            } else if (!initData.is_new && initData.recap) {
              // Returning game — show scene recap, unlock chat immediately
              if (initData.scene_id) {
                setSceneId(initData.scene_id);
                setSceneName(initData.scene_id);
              }
              setMessages(prev => [...prev, {
                role: 'assistant',
                sender: 'Game Master',
                text: initData.recap,
                timestamp: new Date().toISOString(),
                isSystem: false
              }]);
              setSaiReady(true);
            }

            // Set map immediately if returned from init
            if (initData.map && initData.map.tiles) {
              setMapData(initData.map);
            }

            // Restore persisted game state
            if (initData.game_state) {
              const gs = initData.game_state;
              if (gs.current_hp !== null && gs.current_hp !== undefined) setLiveHp(gs.current_hp);
              if (gs.spells_prepared && gs.spells_prepared.length > 0) setLiveSpells(gs.spells_prepared);
              if (gs.used_spells && gs.used_spells.length > 0) setUsedSpells(new Set(gs.used_spells));
              if (gs.extra_items && gs.extra_items.length > 0) setExtraItems(gs.extra_items);
              if (gs.removed_items && gs.removed_items.length > 0) setRemovedItems(new Set(gs.removed_items));
              if (gs.base_updates && gs.base_updates.length > 0) setBaseUpdates(gs.base_updates);
            }
          } catch (e) {
            console.error('realm/init failed:', e);
            // Fallback: unlock chat even if init fails so player isn't stuck
            setSaiReady(true);
          }
        }
        showToast(`✅ File loaded: ${data.filename}`);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast(`❌ Upload failed: ${error.message}`);
      // Clear loading message on error
      setMessages(prev => [
        ...prev.filter(m => !m.loadingLore),
        {
          role: 'system',
          text: `❌ LORE failed to load: ${error.message}`,
          timestamp: new Date().toISOString(),
          isSystem: true
        }
      ]);
      // Clear the file state on error
      if (fileType === 'keys') {
        setKeysFile(null); setKeysLoaded(false); setKeysValidation(null);
      } else if (fileType === 'lore') {
        setLoreFile(null);
      } else if (fileType === 'adventure') {
        setAdventureFile(null); setAdventureLoaded(false); setCharacterLoaded(false); setCharacterFile(null); setCharacterFirstName(null);
      } else if (fileType === 'character') {
        setCharacterFile(null); setCharacterLoaded(false); setCharacterFirstName(null); setSaiReady(false);
      }
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const deleteFile = async (fileType) => {
    const uploadKey = fileType;
    const currentFile = fileType === 'keys' ? keysFile : fileType === 'lore' ? loreFile : fileType === 'adventure' ? adventureFile : characterFile;

    if (!currentFile) return;

    setUploading(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          realm_id: realmId,
          session_id: SESSION_ID,
          file_type: fileType,
          filename: currentFile.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Delete failed: ${response.status}`);
      }

      // Clear state — deleting cascades downstream
      if (fileType === 'keys') {
        setKeysFile(null); setKeysLoaded(false); setKeysValidation(null);
        setLoreFile(null); setLoreLoaded(false);
        setAdventureFile(null); setAdventureLoaded(false);
        setCharacterFile(null); setCharacterLoaded(false); setCharacterFirstName(null); setSaiReady(false);
      } else if (fileType === 'lore') {
        setLoreFile(null);
        setLoreLoaded(false);
        setAdventureFile(null); setAdventureLoaded(false);
        setCharacterFile(null); setCharacterLoaded(false); setCharacterFirstName(null); setSaiReady(false);
      } else if (fileType === 'adventure') {
        setAdventureFile(null); setAdventureLoaded(false); setCharacterLoaded(false); setCharacterFile(null); setCharacterFirstName(null);
      } else if (fileType === 'character') {
        setCharacterFile(null); setCharacterLoaded(false); setCharacterFirstName(null); setSaiReady(false);
      }
      showToast(`✅ File removed`);
    } catch (error) {
      console.error('Delete error:', error);
      showToast(`❌ Remove failed: ${error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, dropRef) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e, dropRef) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.classList.remove('drag-over');
    }
  };

  const handleDrop = (e, fileType, dropRef) => {
    e.preventDefault();
    e.stopPropagation();

    if (dropRef.current) {
      dropRef.current.classList.remove('drag-over');
    }

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0], fileType);
    }
  };

  // ========================================================================
  // Scene Image Generation
  // ========================================================================
  const refreshSessionImages = async () => {
    if (!realmId || !SESSION_ID) return;
    try {
      const res = await fetch(`${BACKEND_URL}/session-images?realm_id=${realmId}&session_id=${SESSION_ID}`);
      const data = await res.json();
      if (data.success) setSessionImages(data.images || []);
    } catch (err) {
      console.error('Failed to load session images:', err);
    }
  };

  const handleViewImageFromGallery = async (imageId) => {
    setImageGalleryOpen(false);
    try {
      const res = await fetch(`${BACKEND_URL}/get-image?realm_id=${realmId}&session_id=${SESSION_ID}&id=${imageId}`);
      const data = await res.json();
      if (data.success) {
        setLightboxImage({ b64: data.image_b64, turn: data.turn_number, prompt: data.prompt_used, title: data.title, id: data.image_id, subject: data.subject });
      }
    } catch (err) {
      console.error('Failed to load image:', err);
    }
  };

  const handleImageClick = async (msgIndex, narrative, msgTurn) => {
    // Show subject picker — fetch subjects from backend
    setSubjectPicker({ msgIndex, narrative, turn: msgTurn, subjects: [], loading: true });
    try {
      const res = await fetch(`${BACKEND_URL}/image-subjects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          realm_id: realmId,
          session_id: SESSION_ID,
          scene_id: sceneId,
          narrative: narrative,
          message_turn: msgTurn,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubjectPicker(prev => prev ? { ...prev, subjects: data.subjects, loading: false } : null);
      } else {
        // Fallback: just scene + custom
        setSubjectPicker(prev => prev ? { ...prev, subjects: [
          { id: 'scene', label: 'Full Scene', description: 'The complete scene' },
          { id: 'custom', label: 'Custom...', description: 'Describe what you want illustrated' }
        ], loading: false } : null);
      }
    } catch (err) {
      setSubjectPicker(prev => prev ? { ...prev, subjects: [
        { id: 'scene', label: 'Full Scene', description: 'The complete scene' },
        { id: 'custom', label: 'Custom...', description: 'Describe what you want illustrated' }
      ], loading: false } : null);
    }
  };

  const handleSubjectSelect = (subject) => {
    if (!subjectPicker) return;
    const { msgIndex, narrative, turn } = subjectPicker;
    setSubjectPicker(null);
    if (subject.id === 'custom') {
      setCustomImagePrompt({ msgIndex, narrative, turn });
      setCustomImageText('');
    } else {
      generateImage(msgIndex, narrative, turn, subject.id, subject.description || '');
    }
  };

  const handleCustomImageSubmit = () => {
    if (!customImagePrompt || !customImageText.trim()) return;
    const { msgIndex, narrative, turn } = customImagePrompt;
    setCustomImagePrompt(null);
    generateImage(msgIndex, narrative, turn, 'custom', customImageText.trim());
    setCustomImageText('');
  };

  const generateImage = async (msgIndex, narrative, msgTurn, subject = 'scene', subjectDesc = '') => {
    setImageLoading(msgIndex);
    try {
      const res = await fetch(`${BACKEND_URL}/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          realm_id: realmId,
          session_id: SESSION_ID,
          narrative: narrative,
          scene_id: sceneId,
          subject: subject,
          subject_description: subjectDesc,
          message_turn: msgTurn,
        }),
      });
      const data = await res.json();
      if (data.success && data.image_b64) {
        const imageData = { b64: data.image_b64, turn: data.turn_number, prompt: data.prompt_used, title: data.title, id: data.image_id, subject: data.subject };
        setLightboxImage(imageData);
        refreshSessionImages();
      } else {
        console.error('Image generation failed:', data.error);
        setToastMessage(data.error || 'Image generation failed');
      }
    } catch (err) {
      console.error('Image generation error:', err);
      setToastMessage('Image generation failed — check connection');
    } finally {
      setImageLoading(null);
    }
  };

  const handleDownloadImage = () => {
    if (!lightboxImage) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${lightboxImage.b64}`;
    const safeName = (lightboxImage.title || 'scene').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-').toLowerCase();
    link.download = `${safeName}-t${lightboxImage.turn}.png`;
    link.click();
    if (lightboxImage.id) {
      fetch(`${BACKEND_URL}/download-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          realm_id: realmId,
          session_id: SESSION_ID,
          image_id: lightboxImage.id,
        }),
      }).then(() => refreshSessionImages()).catch(() => {});
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!input.trim() || !connected) return;

    setLastPlayerMessage(input.trim());
    setLoading(true);
    setAiPhase('thinking');
    
    try {
      // Add user message to UI immediately so the player sees their own input
      setMessages(prev => [...prev, {
        role: 'player',
        text: input,
        sender: username,
        timestamp: new Date().toISOString(),
        isSystem: false
      }]);

      // Send via Socket.IO, include username with message
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('chat', {
          session_id: SESSION_ID,
          realm_id: realmId,
          scene_id: sceneId,
          message: input,
          target: target,
          username: username
        });
      }
      
      setInput('');
    } catch (error) {
      console.error('Send error:', error);
      const errorMsg = {
        role: 'system',
        text: `Error: ${error.message}`,
        timestamp: new Date().toISOString(),
        isSystem: true
      };
      setMessages(prev => [...prev, errorMsg]);
      setLoading(false);
    }
  };

  const getScopeLabel = (scope) => {
    if (scope === 'realm') return '🌍 Realm';
    if (scope === 'storyteller') return '🎭 Game Master';
    return '🎭 Scene';
  };

  // Show loading state while checking localStorage
  if (isLoading) {
    return (
      <div className="app">
        <div className="scene-background">
          <div className="scene-content">
            <div className="scene-title">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!username) {
    return (
      <div className="app">
        {/* Background / Scene View */}
        <div className="scene-background">
          <div className="scene-content">
            <div className="scene-title">Lore Keepers</div>
            <div className="scene-description">
              A place of mystery and wonder...
            </div>
          </div>
        </div>

        {/* Login Modal Overlay */}
        <div className="login-overlay">
          <div className="login-modal">
            <div className="login-header">
              <h2>🎭 Welcome to Lore Keepers</h2>
              <p className="login-subtitle">Enter your name to begin your adventure</p>
            </div>

            <form className="login-form" onSubmit={handleLogin}>
              <input
                type="text"
                className="login-input"
                placeholder="Your username..."
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                maxLength={50}
                autoFocus
              />
              <button
                type="submit"
                className="login-button"
                disabled={!loginInput.trim()}
              >
                Enter the Realm
              </button>
            </form>

            <div className="login-footer">
              <p className="login-hint">You can change your name later via logout.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Background / Scene View */}
      <div className="scene-background">
        <div className="scene-content">
          <div className="scene-title">{sceneId}</div>
          <div className="scene-description">
            A place of mystery and wonder...
          </div>
        </div>
      </div>

      {/* ================================================================
           CHARACTER SHEET DRAWER (slide-out overlay)
         ================================================================ */}
      {characterData && characterLoaded && (
        <>
          {/* Backdrop */}
          {sheetOpen && <div className="drawer-backdrop" onClick={() => setSheetOpen(false)} />}

          {/* Drawer */}
          <div className={`cs-drawer ${sheetOpen ? 'open' : ''}`}>

            {/* Tab handle on the right edge (visible when closed) */}
            <div className="cs-drawer-tab" onClick={() => setSheetOpen(!sheetOpen)}>
              <span className="cs-drawer-tab-text">{sheetOpen ? '✕' : '📜'}</span>
              {!sheetOpen && <span className="cs-drawer-tab-name">{characterFirstName || 'Sheet'}</span>}
            </div>

            <div className="character-sheet">

              {/* ---- BANNER ---- */}
              <div className="cs-banner">
                <div className="cs-charname">{characterData.name || 'Adventurer'}</div>
                <div className="cs-subtitle">
                  {characterData.race || 'Human'} {characterData.class || 'Adventurer'} &middot; Level {characterData.level || 1}
                  {characterData.alignment && <> &middot; {characterData.alignment}</>}
                </div>
              </div>

              {/* ---- TABS ---- */}
              <div className="cs-tabs">
                {[
                  ['stats',    'Stats'],
                  ['spells',   'Spells'],
                  ['gear',     'Gear'],
                  ['traits',   'Traits'],
                ].map(([id, label]) => (
                  <div
                    key={id}
                    className={`cs-tab ${sheetTab === id ? 'active' : ''}`}
                    onClick={() => setSheetTab(id)}
                  >{label}</div>
                ))}
              </div>

              {/* ---- TAB CONTENT ---- */}
              <div className="cs-body">

                {/* ======== STATS TAB ======== */}
                {sheetTab === 'stats' && (
                  <>
                    {/* Ability Scores */}
                    <div className="cs-stat-grid">
                      {[
                        ['str', 'STR'], ['dex', 'DEX'], ['con', 'CON'],
                        ['int', 'INT'], ['wis', 'WIS'], ['cha', 'CHA']
                      ].map(([key, label]) => {
                        const val = characterData[key] || 10;
                        const mod = Math.floor((val - 10) / 2);
                        return (
                          <div className="cs-shield" key={key}>
                            <div className="cs-shield-label">{label}</div>
                            <div className="cs-shield-score">{val}</div>
                            <div className="cs-shield-mod">{mod >= 0 ? '+' : ''}{mod}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Combat Row */}
                    <div className="cs-combat-row">
                      <div className="cs-combat-box">
                        <div className="cs-combat-val">{characterData.ac || 10}</div>
                        <div className="cs-combat-lbl">ARMOR CLASS</div>
                      </div>
                      <div className={`cs-combat-box cs-hp-box ${(() => { const hp = liveHp ?? characterData.current_hp ?? characterData.max_hp ?? 10; const max = characterData.max_hp || 10; return hp <= max * 0.25 ? 'hp-critical' : hp < max ? 'hp-hurt' : ''; })()}`}>
                        <div className="cs-combat-val">{liveHp ?? characterData.current_hp ?? characterData.max_hp ?? 10} / {characterData.max_hp || 10}</div>
                        <div className="cs-combat-lbl">HIT POINTS</div>
                      </div>
                    </div>
                    <div className="cs-combat-row">
                      <div className="cs-combat-box">
                        <div className="cs-combat-val">{characterData.speed || '30 ft.'}</div>
                        <div className="cs-combat-lbl">SPEED</div>
                      </div>
                      <div className="cs-combat-box">
                        <div className="cs-combat-val">{characterData.proficiency_bonus || '+2'}</div>
                        <div className="cs-combat-lbl">PROFICIENCY</div>
                      </div>
                    </div>

                    {/* Skills */}
                    {characterData.skills && characterData.skills.length > 0 && (
                      <div className="cs-box">
                        <div className="cs-box-title">SKILLS</div>
                        <div className="cs-box-content">
                          {characterData.skills.map((s, i) => <div className="cs-ruled-line" key={i}>{s}</div>)}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ======== SPELLS TAB ======== */}
                {sheetTab === 'spells' && (
                  <>
                    {(() => {
                      const spells = liveSpells || characterData.spells || [];
                      return spells.length > 0 ? (
                        <div className="cs-box">
                          <div className="cs-box-title">{liveSpells ? 'PREPARED SPELLS' : 'ATTACKS & SPELLCASTING'}</div>
                          <div className="cs-box-content">
                            {spells.map((s, i) => {
                              const spellName = s.split(' — ')[0].split(' (')[0].trim();
                              const isUsed = usedSpells.has(spellName);
                              return (
                                <div className={`cs-ruled-line ${isUsed ? 'spell-used' : ''}`} key={i}>
                                  {isUsed && <span className="spell-pip used-pip" />}
                                  {!isUsed && <span className="spell-pip ready-pip" />}
                                  {s}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="cs-empty">No spells prepared</div>
                      );
                    })()}

                    {characterData.class_features && characterData.class_features.length > 0 && (
                      <div className="cs-box">
                        <div className="cs-box-title">CLASS FEATURES & ABILITIES</div>
                        <div className="cs-box-content">
                          {characterData.class_features.map((f, i) => <div className="cs-ruled-line" key={i}>{f}</div>)}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ======== GEAR TAB ======== */}
                {sheetTab === 'gear' && (
                  <>
                    {(() => {
                      // Apply base updates and removals to original equipment
                      const baseItems = (characterData.equipment || [])
                        .filter(e => !removedItems.has(e))
                        .map(e => {
                          const upd = baseUpdates.find(u => e.toLowerCase().includes(u.match.toLowerCase()));
                          return upd ? upd.text : e;
                        });
                      const allItems = [...baseItems, ...extraItems];
                      return allItems.length > 0 ? (
                        <div className="cs-box">
                          <div className="cs-box-title">EQUIPMENT</div>
                          <div className="cs-box-content">
                            {baseItems.map((e, i) => <div className="cs-ruled-line" key={`base-${i}`}>{e}</div>)}
                            {extraItems.map((e, i) => <div className="cs-ruled-line item-new" key={`new-${i}`}>{e}</div>)}
                          </div>
                        </div>
                      ) : (
                        <div className="cs-empty">No equipment</div>
                      );
                    })()}

                    {characterData.proficiencies && characterData.proficiencies.length > 0 && (
                      <div className="cs-box">
                        <div className="cs-box-title">PROFICIENCIES & LANGUAGES</div>
                        <div className="cs-box-content">
                          {characterData.proficiencies.map((p, i) => <div className="cs-ruled-line" key={i}>{p}</div>)}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ======== TRAITS TAB ======== */}
                {sheetTab === 'traits' && (
                  <>
                    {characterData.personality_traits ? (
                      <>
                        <div className="cs-box">
                          <div className="cs-box-title">PERSONALITY TRAITS</div>
                          <div className="cs-box-content cs-box-prose">
                            <div className="cs-ruled-line">{characterData.personality_traits}</div>
                          </div>
                        </div>
                        {characterData.ideals && (
                          <div className="cs-box">
                            <div className="cs-box-title">IDEALS</div>
                            <div className="cs-box-content cs-box-prose">
                              <div className="cs-ruled-line">{characterData.ideals}</div>
                            </div>
                          </div>
                        )}
                        {characterData.bonds && (
                          <div className="cs-box">
                            <div className="cs-box-title">BONDS</div>
                            <div className="cs-box-content cs-box-prose">
                              <div className="cs-ruled-line">{characterData.bonds}</div>
                            </div>
                          </div>
                        )}
                        {characterData.flaws && (
                          <div className="cs-box">
                            <div className="cs-box-title">FLAWS</div>
                            <div className="cs-box-content cs-box-prose">
                              <div className="cs-ruled-line">{characterData.flaws}</div>
                            </div>
                          </div>
                        )}
                        {characterData.appearance && (
                          <div className="cs-box">
                            <div className="cs-box-title">APPEARANCE</div>
                            <div className="cs-box-content cs-box-prose">
                              <div className="cs-ruled-line">{characterData.appearance}</div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="cs-empty">No personality data</div>
                    )}
                  </>
                )}
              </div>

              {/* ---- SCENE (bottom) ---- */}
              <div className="cs-scene">
                {sceneName || sceneId || 'Unknown'}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================
           MAP DRAWER (slide-out from right)
         ================================================================ */}
      <>
        {mapOpen && <div className="map-drawer-backdrop" onClick={() => setMapOpen(false)} />}
        {/* Tab always visible on left edge, stacked below character sheet tab */}
        <div className={`map-drawer-tab ${mapOpen ? 'map-tab-open' : ''}`} onClick={() => setMapOpen(!mapOpen)}>
          <span className="map-drawer-tab-text">{mapOpen ? '✕' : '🗺️'}</span>
          {!mapOpen && <span className="map-drawer-tab-name">Map</span>}
        </div>
        <div className={`map-drawer ${mapOpen ? 'open' : ''}`}>

          <div className="map-header">
            <div className="map-title">{sceneName || sceneId || 'Scene Map'}</div>
            <div className="map-subtitle">Tactical View</div>
          </div>

          {mapData && mapData.tiles ? (
            <>
              <div className="map-grid-wrapper">
                <div
                  className="map-grid"
                  style={{ gridTemplateColumns: `repeat(${mapData.width}, 30px)` }}
                >
                  {mapData.tiles.flat().map((tile, idx) => {
                    const x = idx % mapData.width;
                    const y = Math.floor(idx / mapData.width);
                    const entity = (mapData.entities || []).find(e => e.x === x && e.y === y);
                    return (
                      <div
                        key={idx}
                        className={`map-tile tile-${tile}`}
                        title={entity ? entity.label : tile}
                      >
                        {entity && <span className="map-entity">{entity.icon}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="map-legend">
                {(mapData.entities || []).map((e, i) => (
                  <div className="map-legend-item" key={i}>
                    <span className="map-legend-icon">{e.icon}</span>
                    <span className="map-legend-label">{e.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="map-empty">
              The map will appear once the adventure begins...
            </div>
          )}
        </div>
      </>

      {/* Main Container: sidebar (uploads only) + chat */}
      <div className="app-container">
        {/* Left Sidebar — uploads only, hidden once all loaded */}
        {!(keysLoaded && loreLoaded && adventureLoaded && characterLoaded) && (
        <aside className="sidebar">
            <>
              {/* KEYS Zone */}
              <div
                ref={keysDropRef}
                className={`upload-zone ${keysFile ? 'loaded' : 'unloaded'}`}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, keysDropRef)}
                onDragLeave={(e) => handleDragLeave(e, keysDropRef)}
                onDrop={(e) => handleDrop(e, 'keys', keysDropRef)}
              >
                {keysFile ? (
                  <>
                    <div className="upload-icon loaded-icon">🔑</div>
                    <div className="upload-filename">{keysFile.name}</div>
                    <button
                      className="remove-button"
                      onClick={() => deleteFile('keys')}
                      disabled={uploading.keys}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">🔑</div>
                    <div className="upload-text">Drop KEYS file here</div>
                  </>
                )}
                {uploading.keys && <div className="upload-spinner"></div>}
              </div>

              {/* LORE Zone */}
              <div
                ref={loreDropRef}
                className={`upload-zone ${loreFile ? 'loaded' : 'unloaded'} ${!keysLoaded ? 'disabled' : ''}`}
                title={!keysLoaded ? "Upload KEYS first" : ""}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, loreDropRef)}
                onDragLeave={(e) => handleDragLeave(e, loreDropRef)}
                onDrop={(e) => handleDrop(e, 'lore', loreDropRef)}
              >
                {loreFile ? (
                  <>
                    <div className="upload-icon loaded-icon">📖</div>
                    <div className="upload-filename">{loreFile.name}</div>
                    <button
                      className="remove-button"
                      onClick={() => deleteFile('lore')}
                      disabled={uploading.lore}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">📖</div>
                    <div className="upload-text">Drop LORE file here</div>
                  </>
                )}
                {uploading.lore && <div className="upload-spinner"></div>}
              </div>

              {/* ADVENTURE Zone */}
              <div
                ref={adventureDropRef}
                className={`upload-zone ${adventureFile ? 'loaded' : 'unloaded'} ${!loreLoaded ? 'disabled' : ''}`}
                title={!loreLoaded ? "Upload LORE first" : ""}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, adventureDropRef)}
                onDragLeave={(e) => handleDragLeave(e, adventureDropRef)}
                onDrop={(e) => handleDrop(e, 'adventure', adventureDropRef)}
              >
                {adventureFile ? (
                  <>
                    <div className="upload-icon loaded-icon">⚔️</div>
                    <div className="upload-filename">{adventureFile.name}</div>
                    <button
                      className="remove-button"
                      onClick={() => deleteFile('adventure')}
                      disabled={uploading.adventure}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">⚔️</div>
                    <div className="upload-text">Drop ADVENTURE file here</div>
                  </>
                )}
                {uploading.adventure && <div className="upload-spinner"></div>}
              </div>

              {/* CHARACTER Zone */}
              <div
                ref={characterDropRef}
                className={`upload-zone ${characterFile ? 'loaded' : 'unloaded'} ${!adventureLoaded ? 'disabled' : ''}`}
                title={!adventureLoaded ? "Upload Adventure first" : ""}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, characterDropRef)}
                onDragLeave={(e) => handleDragLeave(e, characterDropRef)}
                onDrop={(e) => handleDrop(e, 'character', characterDropRef)}
              >
                {characterFile ? (
                  <>
                    <div className="upload-icon loaded-icon">👤</div>
                    <div className="upload-filename">{characterFile.name}</div>
                    <button
                      className="remove-button"
                      onClick={() => deleteFile('character')}
                      disabled={uploading.character}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">👤</div>
                    <div className="upload-text">Drop CHARACTER file here</div>
                  </>
                )}
                {uploading.character && <div className="upload-spinner"></div>}
              </div>
            </>
        </aside>
        )}

        {/* Right: Chat Pane */}
        <section className="chat-container">
          {/* Header */}
          <div className="chat-header">
            <div className="header-left">
              <h1>🎭 Lore Keepers</h1>
              <div className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}>
                {connected ? '● Connected' : '● Connecting...'}
              </div>
              <div className="username-display">
                {username && <span className="username-badge">👤 {username}</span>}
              </div>
            </div>
            <div className="header-right">
              <div className="scene-selector">
                <span className="scene-display">📍 {sceneName || sceneId || 'No scene'}</span>
              </div>
              {playtestMode && <button
                className="reset-scene-button"
                onClick={async () => {
                  if (!window.confirm('Reset scene? This clears all history and starts fresh.')) return;
                  try {
                    const res = await fetch(`${BACKEND_URL}/reset-scene`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ realm_id: realmId, session_id: SESSION_ID }),
                    });
                    const data = await res.json();
                    if (data.success) {
                      setMessages([]);
                      setMapData(null);
                      setCombatState(null);
                      setPendingDiceRequest(null);
                      setDiceOpen(false);
                      setUsedSpells(new Set());
                      setLiveSpells(null);
                      setExtraItems([]);
                      setRemovedItems(new Set());
                      setBaseUpdates([]);
                      if (data.character_data) {
                        setCharacterData(data.character_data);
                        setLiveHp(data.character_data.current_hp || data.character_data.max_hp || 10);
                      } else {
                        setLiveHp(null);
                      }
                      if (data.starting_scene) {
                        setSceneId(data.starting_scene.scene_id);
                        setSceneName(data.starting_scene.scene_name || data.starting_scene.scene_id);
                        if (socketRef.current) socketRef.current.emit('scene_change', { scene_id: data.starting_scene.scene_id });
                      }
                    }
                  } catch (e) { console.error('Reset failed:', e); }
                }}
                title="Reset scene — clear history and start fresh"
              >
                🔄 Take it from the top
              </button>}
              <button
                className="logout-button"
                onClick={handleLogout}
                title="Logout and return to login screen"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="messages-pane">
            {messages.length === 0 ? (
              <div className="empty-state">
                <p>Welcome to Lore Keepers</p>
                <p className="subtitle">Begin your adventure...</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                msg.isRulesLawyer ? (
                  <div key={idx} className="message system rules-lawyer-msg">
                    <div style={{display:'flex',alignItems:'center',gap:'12px',padding:'8px 12px'}}>
                      <img src={rulesLawyerIcon} alt="Rules Lawyer" style={{width:'101px',flexShrink:0}} />
                      <div>
                        <div style={{fontWeight:'bold',fontFamily:'"Palatino Linotype",serif',fontSize:'0.9rem',color:'#5a3c14',marginBottom:'4px'}}>Rules Lawyer</div>
                        <div style={{fontFamily:'"Palatino Linotype",serif',fontSize:'0.85rem',color:'#3b2410',fontStyle:'italic'}}>{msg.text}</div>
                      </div>
                    </div>
                  </div>
                ) : <div key={idx} className={`message ${msg.isSystem ? 'system' : msg.role}`}>
                  <div className="message-header">
                    <span className="message-role">
                      {msg.role === 'player' ? `👤 ${characterFirstName || 'You'}` : msg.role === 'assistant' ? '🎭 Game Master' : '⚠️ System'}
                    </span>
                    <span className="message-scope">{getScopeLabel(msg.scope)}</span>
                  </div>
                  <div className="message-text">
                    {msg.isDiceRoll
                      ? <span className="dice-result-inline">🎲 {msg.text.replace(/^\[DICE RESULT\s*/, '').replace(/\]$/, '')}</span>
                      : msg.aiError
                      ? <>
                          {msg.text}
                          {msg.retryAction && (
                            <button className="retry-button" onClick={() => {
                              if (socketRef.current && socketRef.current.connected) {
                                socketRef.current.emit('chat', {
                                  session_id: SESSION_ID,
                                  realm_id: realmId,
                                  scene_id: sceneId,
                                  message: msg.retryAction,
                                  target: 'scene',
                                  username: username
                                });
                                setLoading(true);
                                setMessages(prev => prev.filter(m => m !== msg));
                              }
                            }}>
                              Retry
                            </button>
                          )}
                        </>
                      : msg.role === 'assistant'
                        ? renderMarkdown(msg.text, <>
                            {msg.consequence && (
                              <span
                                key="dice-toggle"
                                className="dice-log-toggle"
                                title="View combat log"
                                onClick={() => setDiceLogOpen(diceLogOpen === idx ? null : idx)}
                              >🎲</span>
                            )}
                            <span
                              key="camera-toggle"
                              className="scene-image-toggle"
                              title="Scene illustrations"
                              onClick={() => handleImageClick(idx, msg.text, msg.turn)}
                            >{imageLoading === idx ? <span className="image-loading-spinner" /> : '📷'}</span>
                          </>)
                        : msg.text}
                  </div>
                  {diceLogOpen === idx && msg.consequence && (
                    <div className="dice-log-panel">
                      <div className="dice-log-header">
                        <span>Combat Log</span>
                        <button className="dice-log-close" onClick={() => setDiceLogOpen(null)}>x</button>
                      </div>
                      <div className="dice-log-body">
                        {msg.consequence
                          .split(/(?<=\.)\s+(?=[A-Z][a-z])/)
                          .filter(s => s.trim())
                          .map((roll, ri) => (
                            <div key={ri} className="dice-log-entry">{roll.trim()}</div>
                          ))
                        }
                      </div>
                      {msg.diceUsed && (
                        <div className="dice-log-summary">
                          Dice used: {Object.entries(msg.diceUsed).map(([die, count]) => `${count}x ${die}`).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            {loading && (
              <div className="thinking-indicator">
                {loadingSeconds < 90 ? (
                  <>
                    <div className="thinking-dots">
                      <span className="thinking-dot" />
                      <span className="thinking-dot" />
                      <span className="thinking-dot" />
                    </div>
                    <span className="thinking-text">
                      {aiPhase === 'narrating'
                        ? 'The tale unfolds...'
                        : 'The Game Master deliberates...'}
                      {loadingSeconds > 5 && <span className="thinking-timer"> ({loadingSeconds}s)</span>}
                    </span>
                  </>
                ) : (
                  <div className="thinking-slow">
                    <span className="thinking-slow-text">The AI is taking a while ({loadingSeconds}s). It may still respond.</span>
                    <div className="thinking-slow-actions">
                      <button className="retry-button" onClick={() => {
                        if (socketRef.current && socketRef.current.connected && lastPlayerMessage) {
                          socketRef.current.emit('chat', {
                            session_id: SESSION_ID,
                            realm_id: realmId,
                            scene_id: sceneId,
                            message: lastPlayerMessage,
                            target: 'scene',
                            username: username
                          });
                          setLoadingSeconds(0);
                        }
                      }}>🔄 Retry</button>
                      <button className="wait-button" onClick={() => setLoadingSeconds(0)}>⏳ Keep Waiting</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Roll Required Banner */}
          {pendingDiceRequest && (
            <div className="roll-required-banner" onClick={() => setDiceOpen(true)}>
              <img src={gruntIcon} alt="Grunt" className="roll-required-icon-img" />
              <span className="roll-required-text">
                {pendingDiceRequest.description || `Roll D${pendingDiceRequest.sides}`}
              </span>
              <span className="roll-required-hint">tap to roll</span>
            </div>
          )}

          {/* Input Section */}
          <div className="input-section">
            <div className="target-selector">
              <label>Send to:</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="target-select"
              >
                <option value="scene">🎭 Scene (Local)</option>
                <option value="realm">🌍 Realm (All)</option>
                <option value="storyteller">🎭 Game Master (Private)</option>
              </select>
              {sessionImages.length > 0 && (
                <div className="image-gallery-wrapper">
                  <button
                    className={`image-gallery-btn${sessionImages.some(i => i.expiring && !i.downloaded) ? ' has-expiring' : ''}`}
                    onClick={() => { setImageGalleryOpen(!imageGalleryOpen); refreshSessionImages(); }}
                    title="Scene illustrations"
                  >
                    🖼️ {sessionImages.length}
                  </button>
                  {imageGalleryOpen && (
                    <>
                    <div className="image-gallery-backdrop" onClick={() => setImageGalleryOpen(false)} />
                    <div className="image-gallery-dropdown">
                      <div className="image-gallery-header">Scene Illustrations</div>
                      {sessionImages.map((img, i) => (
                        <div
                          key={i}
                          className={`image-gallery-item${img.expiring && !img.downloaded ? ' expiring' : ''}`}
                          onClick={() => handleViewImageFromGallery(img.id)}
                          title={img.expiring ? `${img.turns_remaining} turns remaining!` : `Turn ${img.turn} — ${img.subject || 'scene'}`}
                        >
                          <span className="image-gallery-icon">{img.subject === 'custom' ? '✏️' : img.subject === 'scene' ? '🌄' : (img.subject || '').startsWith('kill_') ? '⚔️' : '🎨'}</span>
                          <span className="image-gallery-title">{img.title}</span>
                          <span className="image-gallery-turn">T{img.turn}</span>
                          {img.expiring && !img.downloaded && <span className="image-gallery-warn">⚠️</span>}
                          {img.downloaded && <span className="image-gallery-saved">✓</span>}
                        </div>
                      ))}
                    </div>
                    </>
                  )}
                </div>
              )}
              {realmId && <span className="realm-id-display" title={realmId}>{realmId}</span>}
            </div>

            <form className="chat-input-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                className="chat-input"
                placeholder={
                  !characterLoaded
                    ? "⚔️ Upload your Character Sheet to begin..."
                    : !saiReady
                    ? "⏳ The Game Master is preparing your world..."
                    : target === 'storyteller'
                    ? "Ask the Game Master..."
                    : target === 'realm'
                    ? "Say something to everyone..."
                    : "Say something to your group..."
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading || !connected || !characterLoaded || !saiReady}
                autoFocus
              />
              <button
                type="submit"
                className="chat-submit"
                disabled={loading || !input.trim() || !connected || !characterLoaded || !saiReady}
              >
                {loading ? '⏳' : '→'}
              </button>
            </form>
          </div>
        </section>
      </div>

      {/* Dice Tray */}
      <DiceTray
        open={diceOpen}
        onToggle={() => setDiceOpen(!diceOpen)}
        pendingRequest={pendingDiceRequest}
        rollPurpose={combatState?.awaiting}
        onRollSubmit={(rollResult) => {
          // Send dice result as a chat message through Socket.IO
          const valuesStr = rollResult.values.join(', ');
          const rollText = `[DICE RESULT ${rollResult.raw} = ${rollResult.total} (rolled ${valuesStr})]`;

          // Show in chat
          setMessages(prev => [...prev, {
            role: 'player',
            text: rollText,
            sender: username,
            timestamp: new Date().toISOString(),
            isSystem: false,
            isDiceRoll: true
          }]);

          // Send via Socket.IO
          if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit('chat', {
              session_id: SESSION_ID,
              realm_id: realmId,
              scene_id: sceneId,
              message: rollText,
              target: 'scene',
              username: username
            });
            setLoading(true);
          }

          setPendingDiceRequest(null);
        }}
      />

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}

      {/* Scene Image Lightbox */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>✕</button>
            <img
              src={`data:image/png;base64,${lightboxImage.b64}`}
              alt="Scene illustration"
              className="lightbox-image"
            />
            {lightboxImage.title && <div className="lightbox-title">{lightboxImage.title}</div>}
            <div className="lightbox-actions">
              <button className="lightbox-download" onClick={handleDownloadImage}>
                ⬇ Download Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subject Picker */}
      {subjectPicker && (
        <div className="image-choice-overlay" onClick={() => setSubjectPicker(null)}>
          <div className="subject-picker-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="image-choice-title">What would you like illustrated?</div>
            {subjectPicker.loading ? (
              <div className="subject-picker-loading"><span className="image-loading-spinner" /> Analyzing scene...</div>
            ) : (
              <div className="subject-picker-list">
                {subjectPicker.subjects.map((s, i) => (
                  <button key={i} className="subject-picker-item" onClick={() => handleSubjectSelect(s)}
                    title={s.description}>
                    <span className="subject-icon">
                      {s.id === 'scene' ? '🌄' : s.id === 'custom' ? '✏️' : s.id.startsWith('kill_') ? '⚔️' : '🎨'}
                    </span>
                    <span className="subject-label">{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Image Freeform Input */}
      {customImagePrompt && (
        <div className="image-choice-overlay" onClick={() => setCustomImagePrompt(null)}>
          <div className="custom-image-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="image-choice-title">Describe what you want illustrated</div>
            <input
              type="text"
              className="custom-image-input"
              placeholder="A close-up of the enchanted crystal pendant..."
              value={customImageText}
              onChange={(e) => setCustomImageText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomImageSubmit(); }}
              autoFocus
            />
            <div className="image-choice-buttons">
              <button className="image-choice-btn" onClick={() => setCustomImagePrompt(null)}>Cancel</button>
              <button className="image-choice-btn" onClick={handleCustomImageSubmit} disabled={!customImageText.trim()}>
                🎨 Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Expiry Warning Toast */}
      {imageExpiryWarning && (
        <div className="expiry-toast" onClick={() => setImageExpiryWarning(null)}>
          <span className="expiry-icon">⚠️</span>
          <span className="expiry-text">{imageExpiryWarning.message}</span>
          <button className="expiry-dismiss" onClick={(e) => { e.stopPropagation(); setImageExpiryWarning(null); }}>✕</button>
        </div>
      )}
    </div>
  );
}

export default App;
